import { useEffect, useState } from 'react';
import {
  emptyStrategicMatrix,
  emptySwot,
  emptySwotItem,
  normalizeStrategicMatrix,
  type StrategicMatrixRow,
  type SwotData,
  type SwotItem,
} from '@isbd/shared';
import { invokeAiFunction, supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { downloadTextFile } from '@/lib/downloadText';
import { useToast } from '@/features/ui/toast/useToast';
import './pilotage.css';

const INTERNAL: { key: keyof SwotData; label: string }[] = [
  { key: 'strengths', label: 'Forces' },
  { key: 'weaknesses', label: 'Faiblesses' },
];

const EXTERNAL: { key: keyof SwotData; label: string }[] = [
  { key: 'opportunities', label: 'Opportunités' },
  { key: 'threats', label: 'Menaces' },
];

function asSwot(data: Partial<SwotData> | null | undefined): SwotData {
  const base = emptySwot();
  if (!data) return base;
  for (const { key } of [...INTERNAL, ...EXTERNAL]) {
    const arr = data[key];
    base[key] = Array.isArray(arr) ? (arr as SwotItem[]) : [];
  }
  return base;
}

function matrixToCsv(rows: StrategicMatrixRow[]) {
  const header = [
    'Pilier de pilotage',
    'Diagnostic',
    'Intention',
    'Objectifs (SMART)',
    'Actions prioritaires',
    'Indicateurs (KPI)',
  ];
  const escape = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    header.join(';'),
    ...rows.map((r) =>
      [r.pilier, r.diagnostic, r.intention, r.objectifs_smart, r.actions_prioritaires, r.indicateurs_kpi]
        .map(escape)
        .join(';'),
    ),
  ];
  return lines.join('\n');
}

interface Props {
  projectId: string;
}

export function SwotMatrixPanel({ projectId }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [swot, setSwot] = useState<SwotData>(emptySwot);
  const [rows, setRows] = useState<StrategicMatrixRow[]>(emptyStrategicMatrix);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    void (async () => {
      const [s, m] = await Promise.all([
        supabase.from('swot_analyses').select('*').eq('project_id', projectId).maybeSingle(),
        supabase.from('strategic_matrices').select('*').eq('project_id', projectId).maybeSingle(),
      ]);
      if (s.data) setSwot(asSwot(s.data as Partial<SwotData>));
      else setSwot(emptySwot());
      setRows(normalizeStrategicMatrix(m.data?.axes));
    })();
  }, [projectId]);

  function updateItem(section: keyof SwotData, index: number, patch: Partial<SwotItem>) {
    setSwot((prev) => ({
      ...prev,
      [section]: prev[section].map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  }

  async function generateSwot() {
    if (!profile || !projectId) return;
    setLoading(true);
    try {
      const result = await invokeAiFunction<SwotData>('ai-analyze', { kind: 'swot', project_id: projectId });
      const next = asSwot(result);
      setSwot(next);
      const { error: err } = await supabase.from('swot_analyses').upsert(
        { user_id: profile.id, project_id: projectId, ...next },
        { onConflict: 'project_id' },
      );
      if (err) throw err;
      toast.success('SWOT généré — vous pouvez éditer puis enregistrer.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'SWOT impossible');
    } finally {
      setLoading(false);
    }
  }

  async function saveSwot() {
    if (!profile || !projectId) return;
    const { error: err } = await supabase.from('swot_analyses').upsert(
      { user_id: profile.id, project_id: projectId, ...swot },
      { onConflict: 'project_id' },
    );
    if (err) toast.error(err.message);
    else toast.success('SWOT enregistré.');
  }

  async function generateMatrix() {
    if (!profile || !projectId) return;
    setLoading(true);
    try {
      const result = await invokeAiFunction<{ axes: StrategicMatrixRow[] }>('ai-analyze', {
        kind: 'strategic_matrix',
        project_id: projectId,
      });
      const next = normalizeStrategicMatrix(result.axes);
      setRows(next);
      const { error: err } = await supabase.from('strategic_matrices').upsert(
        { user_id: profile.id, project_id: projectId, axes: next },
        { onConflict: 'project_id' },
      );
      if (err) throw err;
      toast.success('Matrice stratégique générée — éditable ci-dessous.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Matrice impossible');
    } finally {
      setLoading(false);
    }
  }

  async function saveMatrix() {
    if (!profile || !projectId) return;
    const { error: err } = await supabase.from('strategic_matrices').upsert(
      { user_id: profile.id, project_id: projectId, axes: rows },
      { onConflict: 'project_id' },
    );
    if (err) toast.error(err.message);
    else toast.success('Matrice enregistrée.');
  }

  function updateRow(index: number, patch: Partial<StrategicMatrixRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function renderSwotSection(key: keyof SwotData, label: string) {
    return (
      <div className="pilotage-swot-cell">
        <div className="pilotage-swot-cell-head">
          <h4>{label}</h4>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setSwot((p) => ({ ...p, [key]: [...p[key], emptySwotItem()] }))}
          >
            + Ligne
          </button>
        </div>
        {swot[key].length === 0 ? <p className="muted">Aucune entrée.</p> : null}
        {swot[key].map((item, idx) => (
          <div className="pilotage-swot-item" key={`${key}-${idx}`}>
            <div className="field">
              <label>Élément</label>
              <input value={item.element} onChange={(e) => updateItem(key, idx, { element: e.target.value })} />
            </div>
            <div className="field">
              <label>Proposition IA</label>
              <input
                value={item.proposition_ia}
                onChange={(e) => updateItem(key, idx, { proposition_ia: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Recommandation</label>
              <textarea
                rows={2}
                value={item.recommandation}
                onChange={(e) => updateItem(key, idx, { recommandation: e.target.value })}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <section className="panel" style={{ marginBottom: '1rem' }}>
        <div className="pilotage-section-head">
          <h3 style={{ marginTop: 0 }}>Analyse SWOT</h3>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button type="button" className="btn btn-accent" disabled={loading} onClick={() => void generateSwot()}>
              {loading ? '…' : 'Générer Grok'}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void saveSwot()}>
              Enregistrer
            </button>
          </div>
        </div>

        <div className="pilotage-swot-grid">
          <div className="pilotage-swot-col">
            <div className="pilotage-swot-col-title">Analyse interne</div>
            {INTERNAL.map(({ key, label }) => (
              <div key={key}>{renderSwotSection(key, label)}</div>
            ))}
          </div>
          <div className="pilotage-swot-col">
            <div className="pilotage-swot-col-title">Analyse externe</div>
            {EXTERNAL.map(({ key, label }) => (
              <div key={key}>{renderSwotSection(key, label)}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="pilotage-section-head">
          <h3 style={{ marginTop: 0 }}>Matrice stratégique</h3>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-accent" disabled={loading} onClick={() => void generateMatrix()}>
              {loading ? '…' : 'Générer Grok'}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void saveMatrix()}>
              Enregistrer
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => downloadTextFile(`matrice-strategique-${projectId.slice(0, 8)}.csv`, matrixToCsv(rows))}
            >
              Télécharger
            </button>
          </div>
        </div>

        <div className="pilotage-matrix-scroll">
          <table className="pilotage-matrix-table">
            <thead>
              <tr>
                <th>Pilier de pilotage</th>
                <th>Diagnostic</th>
                <th>Intention</th>
                <th>Objectifs (SMART)</th>
                <th>Actions prioritaires</th>
                <th>Indicateurs (KPI)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.code}>
                  <td className="pilotage-matrix-pilier">{row.pilier}</td>
                  <td>
                    <textarea
                      rows={3}
                      value={row.diagnostic}
                      onChange={(e) => updateRow(idx, { diagnostic: e.target.value })}
                    />
                  </td>
                  <td>
                    <textarea
                      rows={3}
                      value={row.intention}
                      onChange={(e) => updateRow(idx, { intention: e.target.value })}
                    />
                  </td>
                  <td>
                    <textarea
                      rows={3}
                      value={row.objectifs_smart}
                      onChange={(e) => updateRow(idx, { objectifs_smart: e.target.value })}
                    />
                  </td>
                  <td>
                    <textarea
                      rows={3}
                      value={row.actions_prioritaires}
                      onChange={(e) => updateRow(idx, { actions_prioritaires: e.target.value })}
                    />
                  </td>
                  <td>
                    <textarea
                      rows={3}
                      value={row.indicateurs_kpi}
                      onChange={(e) => updateRow(idx, { indicateurs_kpi: e.target.value })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
